import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  ArrowRight,
  ChartLineUp,
  Gear,
  Images,
  List,
  MagnifyingGlass,
  Megaphone,
  Receipt,
  Storefront,
  Tag,
  Truck,
  Users,
  type Icon,
} from "@phosphor-icons/react";
import type { Permission } from "@clubghor/auth";
import type { ProductDTO, OrderDTO } from "@clubghor/types";
import { api } from "~/lib/api";
import { useSession } from "~/lib/session";
import { cn, formatTaka, formatRelative } from "~/lib/utils";
import { Badge, Kbd } from "~/components/ui/primitives";

/**
 * Command palette.
 *
 * Two jobs, both keyboard-first:
 *   1. Jump to any section the role can reach.
 *   2. Find a specific order by number, or a product by name, and go straight to
 *      it. In an order queue this is the difference between three clicks and one
 *      keystroke.
 *
 * Built on Radix Dialog, so focus trapping, restore and Escape come for free
 * rather than being hand-rolled.
 */

interface JumpItem {
  label: string;
  to: string;
  icon: Icon;
  permission: Permission;
  keywords?: string;
}

const JUMPS: JumpItem[] = [
  { label: "Dashboard", to: "/", icon: ChartLineUp, permission: "dashboard.read" },
  { label: "Orders", to: "/orders", icon: Receipt, permission: "orders.read", keywords: "sales purchases" },
  { label: "Products", to: "/products", icon: Storefront, permission: "products.read", keywords: "catalogue items stock" },
  { label: "Categories", to: "/categories", icon: List, permission: "categories.read" },
  { label: "Coupons", to: "/coupons", icon: Tag, permission: "coupons.read", keywords: "discount promo code" },
  { label: "Customers", to: "/customers", icon: Users, permission: "customers.read", keywords: "buyers phone" },
  { label: "Delivery areas", to: "/delivery-areas", icon: Truck, permission: "delivery.read", keywords: "shipping zones fees" },
  { label: "Media library", to: "/media", icon: Images, permission: "media.write", keywords: "images upload r2" },
  { label: "Staff accounts", to: "/staff", icon: Users, permission: "users.manage", keywords: "team roles editors admins accounts" },
  { label: "Storefront settings", to: "/settings", icon: Megaphone, permission: "settings.read", keywords: "contact banner ticker hero" },
  { label: "Your account", to: "/profile", icon: Gear, permission: "dashboard.read", keywords: "password profile" },
];

export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate();
  const { can } = useSession();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  // Reset between openings, so the palette never reopens mid-search.
  useEffect(() => {
    if (!open) {
      setQuery("");
      setActiveIndex(0);
    }
  }, [open]);

  const trimmed = query.trim();

  const jumps = useMemo(
    () =>
      JUMPS.filter((item) => can(item.permission)).filter((item) => {
        if (!trimmed) return true;
        const haystack = `${item.label} ${item.keywords ?? ""}`.toLowerCase();
        return haystack.includes(trimmed.toLowerCase());
      }),
    [can, trimmed],
  );

  /**
   * Only search records once there is something to search for, and only in the
   * two resources where "find the one I mean" is the actual task. The request is
   * lazy: an empty palette costs no network.
   */
  const orderSearch = useQuery({
    queryKey: ["palette", "orders", trimmed],
    queryFn: () => api.list<OrderDTO>("/api/orders", { q: trimmed, per_page: 5 }),
    enabled: open && trimmed.length >= 2 && can("orders.read"),
    staleTime: 20_000,
  });

  const productSearch = useQuery({
    queryKey: ["palette", "products", trimmed],
    queryFn: () => api.list<ProductDTO>("/api/products", { q: trimmed, per_page: 5 }),
    enabled: open && trimmed.length >= 2 && can("products.read"),
    staleTime: 20_000,
  });

  const orderHits = orderSearch.data?.rows ?? [];
  const productHits = productSearch.data?.rows ?? [];

  /** One flat list, so arrow keys traverse sections without special cases. */
  const flat = useMemo(
    () => [
      ...jumps.map((item) => ({ kind: "jump" as const, item })),
      ...orderHits.map((order) => ({ kind: "order" as const, order })),
      ...productHits.map((product) => ({ kind: "product" as const, product })),
    ],
    [jumps, orderHits, productHits],
  );

  useEffect(() => {
    setActiveIndex(0);
  }, [trimmed, flat.length]);

  function go(index: number) {
    const entry = flat[index];
    if (!entry) return;

    if (entry.kind === "jump") {
      void navigate({ to: entry.item.to });
    } else if (entry.kind === "order") {
      void navigate({ to: "/orders/$id", params: { id: String(entry.order.id) } });
    } else {
      void navigate({ to: "/products/$id", params: { id: String(entry.product.id) } });
    }
    onOpenChange(false);
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % Math.max(flat.length, 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + flat.length) % Math.max(flat.length, 1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      go(activeIndex);
    }
  }

  const searching = trimmed.length >= 2;
  const showEmpty = searching && flat.length === 0 && !orderSearch.isFetching && !productSearch.isFetching;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[oklch(0_0_0/0.42)] transition-opacity duration-150" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onKeyDown={onKeyDown}
          className={cn(
            "glass-overlay fixed left-1/2 top-[12vh] z-50 w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2",
            "flex max-h-[70dvh] flex-col overflow-hidden rounded-[var(--admin-radius-card)]",
          )}
        >
          <DialogPrimitive.Title className="sr-only">Search and jump to</DialogPrimitive.Title>

          <div className="flex items-center gap-2.5 border-b border-[var(--cg-line)] px-4">
            <MagnifyingGlass className="size-4 shrink-0 text-[var(--cg-content-muted)]" />
            {/*
              A real combobox. Previously this input carried only an aria-label and
              arrow keys moved a background colour, so a screen reader announced no
              list, no position and no highlight. aria-activedescendant is what ties
              the input to the visually-highlighted option.
            */}
            <input
              // eslint-disable-next-line jsx-a11y/no-autofocus -- a command palette is
              // the one place autofocus is correct: it is opened BY a keystroke and
              // exists solely to receive typing.
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search orders, products, or jump to a section"
              aria-label="Search orders, products, or sections"
              role="combobox"
              aria-expanded="true"
              aria-controls="palette-listbox"
              aria-autocomplete="list"
              aria-activedescendant={flat.length > 0 ? `palette-option-${activeIndex}` : undefined}
              className="h-12 w-full bg-transparent text-sm text-[var(--cg-content)] outline-none placeholder:text-[var(--cg-content-subtle)]"
            />
            <Kbd>Esc</Kbd>
          </div>

          <div id="palette-listbox" role="listbox" aria-label="Results" className="scrollbar-thin flex-1 overflow-y-auto p-2">
            {jumps.length > 0 && (
              <Group label={trimmed ? "Sections" : "Jump to"}>
                {jumps.map((item, index) => (
                  <Row key={item.to} index={index} active={activeIndex === index} onSelect={() => go(index)} onHover={() => setActiveIndex(index)}>
                    <item.icon className="size-4 shrink-0 text-[var(--cg-content-muted)]" />
                    <span className="truncate">{item.label}</span>
                    <ArrowRight className="ml-auto size-3.5 shrink-0 text-[var(--cg-content-subtle)]" />
                  </Row>
                ))}
              </Group>
            )}

            {orderHits.length > 0 && (
              <Group label="Orders">
                {orderHits.map((order, offset) => {
                  const index = jumps.length + offset;
                  return (
                    <Row key={order.id} index={index} active={activeIndex === index} onSelect={() => go(index)} onHover={() => setActiveIndex(index)}>
                      <Receipt className="size-4 shrink-0 text-[var(--cg-content-muted)]" />
                      <span className="tabular shrink-0 font-medium">{order.orderNumber}</span>
                      <span className="truncate text-[var(--cg-content-muted)]">{order.customerName}</span>
                      <span className="ml-auto flex shrink-0 items-center gap-2">
                        <span className="tabular text-[var(--cg-content-muted)]">{formatTaka(order.total)}</span>
                        <Badge tone="neutral">{formatRelative(order.createdAt)}</Badge>
                      </span>
                    </Row>
                  );
                })}
              </Group>
            )}

            {productHits.length > 0 && (
              <Group label="Products">
                {productHits.map((product, offset) => {
                  const index = jumps.length + orderHits.length + offset;
                  return (
                    <Row key={product.id} index={index} active={activeIndex === index} onSelect={() => go(index)} onHover={() => setActiveIndex(index)}>
                      <Storefront className="size-4 shrink-0 text-[var(--cg-content-muted)]" />
                      <span className="truncate">{product.name}</span>
                      <span className="ml-auto flex shrink-0 items-center gap-2">
                        <span className="tabular text-[var(--cg-content-muted)]">{formatTaka(product.effectivePrice)}</span>
                        {!product.isActive && <Badge tone="danger">Inactive</Badge>}
                      </span>
                    </Row>
                  );
                })}
              </Group>
            )}

            {(orderSearch.isFetching || productSearch.isFetching) && (
              <p className="px-3 py-6 text-center text-[0.8125rem] text-[var(--cg-content-muted)]">Searching…</p>
            )}

            {showEmpty &&
              (orderSearch.isError || productSearch.isError ? (
                // A failed search is NOT the same as no matches; saying "nothing
                // matches" when the request failed sends the operator looking for a
                // typo that is not there.
                <p className="px-3 py-8 text-center text-[0.8125rem] text-[var(--cg-content-muted)]">
                  Search could not reach the server. Check your connection and try again.
                </p>
              ) : (
                <p className="px-3 py-8 text-center text-[0.8125rem] text-[var(--cg-content-muted)]">
                  Nothing matches “{trimmed}”.
                </p>
              ))}

            {!searching && (
              <p className="px-3 py-2 text-center text-xs text-[var(--cg-content-subtle)]">
                Type at least two characters to search orders and products.
              </p>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-1.5 last:mb-0">
      <p className="px-2.5 py-1.5 text-[0.625rem] font-bold uppercase tracking-[0.14em] text-[var(--cg-content-subtle)]">
        {label}
      </p>
      <ul className="flex flex-col gap-0.5">{children}</ul>
    </div>
  );
}

function Row({
  index,
  active,
  onSelect,
  onHover,
  children,
}: {
  index: number;
  active: boolean;
  onSelect: () => void;
  onHover: () => void;
  children: React.ReactNode;
}) {
  return (
    <li
      id={`palette-option-${index}`}
      role="option"
      // aria-selected is what actually announces the position to a screen reader;
      // the background colour alone conveyed it to sighted users only.
      aria-selected={active}
    >
      <button
        type="button"
        onClick={onSelect}
        // Keyboard and pointer share one highlight, so the "current" row is never
        // ambiguous. Previously this was a no-op, so hovering and arrowing could
        // point at two different rows at once.
        onMouseEnter={onHover}
        tabIndex={-1}
        className={cn(
          "flex w-full items-center gap-2.5 rounded-[var(--admin-radius-chip)] px-2.5 py-2 text-left",
          "text-[0.8125rem] text-[var(--cg-content)]",
          "transition-colors duration-100 ease-[cubic-bezier(0.2,0,0,1)]",
          active && "bg-[color-mix(in_oklab,var(--cg-primary)_14%,transparent)]",
        )}
      >
        {children}
      </button>
    </li>
  );
}
